import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { UPDATE_PROJECT } from '../../graphql/mutations';
import { CampaignEditor } from './CampaignEditor';
import { WritePost } from './WritePost';
import { showInkToast } from '../cms/InkToast';
import { InkSkeleton } from '../cms/InkSkeleton';
import { AgentFirstButton, openAskAgent } from '../cms/AskYourAgent';
import { CampaignMenuNote } from '../cms/Sections';
import { AlwaysOnEditor } from './AlwaysOnEditor';
import { ApprovalChip } from '../settings/AgentControls';
import { approvalsAt, ruleAnchor } from '../settings/goesOut';
import { endedAs, neverApproved, Phase, phaseOf } from './campaignPhase';
import { CampaignResults } from './CampaignResults';
import { CampaignColorPicker } from './CampaignColorPicker';
import { CampaignBoardView, CampaignLinksRow, CampaignTeamRow } from './CampaignBoard';
import { useChannelReconnect } from './ChannelPicker';
import { todayWindow } from '../cms/todayData';
import {
  cadenceWords,
  joinWords,
  legacyFilterWords,
  onlyOnWords,
  sentenceFromConditions,
} from './alwaysOnSentence';
import {
  Board,
  CAMPAIGN_BOARD,
  CAMPAIGN_LIST,
  draftCampaignWords,
  firstName,
  lostChannelWords,
} from './campaignCards';
import {
  AlwaysOnConditions,
  APPROVE_CAMPAIGN,
  channelMark,
  channelAccount,
  channelLabel,
  channelName,
  Channel,
  connectionExpired,
  dayTime,
  REACH_CHANNEL_ROWS,
  REACH_POST_FIELDS,
  ReachPost,
  postText,
  postTime,
  calendarDate,
  campaignStart,
  toDate,
  campaignRange,
  disconnectedRuleChannels,
  shortDate,
} from './reach';
import { askInk } from '../cms/InkConfirm';
import { CAMPAIGN_VOICE, campaignVoiceLine, VoiceCheck } from './voiceWords';
import { andList } from '../cms/pageChecks';
import { POST_STATUS, TO_APPROVE } from '../statusWords';

/**
 * Reach → Campaigns ("tinycms UX optimization review v6", Prototype v6): the
 * dated campaigns and the always-on ones on the left; the selected one on the
 * right. Both are `projects`: a dated campaign has tactics the person approves
 * in one go; an always-on campaign fires on conditions (what the old automation
 * rules were) and can be paused, resumed or changed here.
 */

const REACH_CAMPAIGNS = gql`
  query ReachCampaigns($accountId: ID!) {
    projects(accountId: $accountId) {
      id
      name
      kind
      status
      deadline
      startDate
      campaignStory
      landingPageUrl
      landingPageStatus
      color
      plan
      createdBy
      createdAt
      conditions
      channels {
        id
        platform
        profileId
        profileName
        profileUsername
        approvalMode
        isActive
        isPublishing
      }
      approvalMode
      recentPosts { ${REACH_POST_FIELDS} }
      posts {
        id
        title
        content
        status
        scheduledDate
        publishedAt
        targets {
          id
          platform
        }
      }
    }
  }
`;

interface CampaignPost {
  id: string;
  title: string;
  content: string;
  status: string;
  scheduledDate?: string | null;
  publishedAt?: string | null;
  targets: Array<{ id: string; platform: string }>;
}
interface CampaignChannel {
  id: string;
  platform: string;
  profileId?: string | null;
  profileName?: string | null;
  profileUsername?: string | null;
  approvalMode?: string | null;
  /** False once disconnected: the rule keeps it, but nothing posts there. */
  isActive?: boolean | null;
  isPublishing?: boolean | null;
}
interface Campaign {
  /** An automatic rule's posts: ask | out (Account → Approvals). */
  approvalMode?: string | null;
  id: string;
  name: string;
  kind: 'campaign' | 'always_on';
  status: string;
  deadline?: string | null;
  startDate?: string | null;
  campaignStory?: string | null;
  landingPageUrl?: string | null;
  landingPageStatus?: string | null;
  /** A campaign color key (campaignColors.ts); null → spread by id. */
  color?: string | null;
  plan?: string | null;
  /** Null when the site's agent made it. */
  createdBy?: string | null;
  createdAt: string;
  conditions?: AlwaysOnConditions | null;
  channels: CampaignChannel[];
  recentPosts: ReachPost[];
  posts: CampaignPost[];
}

// Drafts first: set up but not scheduled yet (Prototype; feedback 71704394).
type RailGroup = Phase | 'draft';
const PHASES: Array<[RailGroup, string]> = [
  ['draft', 'DRAFTS'],
  ['running', 'RUNNING'],
  ['upcoming', 'UPCOMING'],
  ['ended', 'ENDED'],
];
const endTime = (c: Campaign) => calendarDate(c.deadline)?.getTime() ?? Infinity;

const REACH_STATUS: Record<string, { label: string; color: string }> = {
  posted: { label: POST_STATUS.posted, color: 'var(--ink-green-ink)' },
  publishing: { label: POST_STATUS.publishing, color: 'var(--ink-green-ink)' },
  scheduled: { label: POST_STATUS.scheduled, color: 'var(--ink-accent)' },
  waiting: { label: POST_STATUS.waiting, color: 'var(--ink-amber)' },
  skipped: { label: POST_STATUS.skipped, color: 'var(--ink-ink3)' },
  draft: { label: POST_STATUS.draft, color: 'var(--ink-ink3)' },
  missed: { label: POST_STATUS.missed, color: 'var(--ink-red)' },
};

// "Draft" as on Today and for posts and articles (backlog d0a032ef: "Planning" here).
const CAMPAIGN_STATUS: Record<string, string> = {
  planning: 'Draft',
  review: 'In review',
  active: 'Running',
  completed: 'Finished',
};

// "Jan 13", with the year when it isn't this year.
// A campaign's end date is a calendar day (calendarDate), not a moment.
const day = (v?: string | null) => shortDate(calendarDate(v));
const hm = (v: string) => v.slice(0, 5);

// "Photo posts, for articles up to 48 hours old" (backlog e6a9c0a2).
const POST_AS: Record<string, string> = {
  auto: 'Link or text posts',
  post: 'Link or text posts',
  link: 'Link posts',
  text: 'Text posts',
  photo: 'Photo posts',
  video: 'Video posts',
  random: 'A mix of link and photo posts',
};
const KIND_WORD: Record<string, string> = { bookmark: 'link', photo: 'photo', video: 'video' };

/**
 * The always-on conditions in words, for the rule detail's rows, in the
 * sentence's own wording and its one time format ("9 AM"). onlyOn is null
 * when the rule posts on any day.
 */
function describeConditions(k: AlwaysOnConditions | null | undefined) {
  const c = k || {};
  const topics = c.include?.length ? c.include.join(', ') : 'Every article';
  const but = c.exclude?.length ? ` (not ${c.exclude.join(', ')})` : '';
  const kinds = c.mediaTypes?.length
    ? ` · only ${joinWords(
        c.mediaTypes.map((m) => KIND_WORD[m.toLowerCase()] || m.toLowerCase()),
        'or'
      )} articles`
    : '';
  const sched = c.schedule || [];
  const mode: 'asap' | 'times' | 'interval' | 'marks' = sched.some((e) => e.time)
    ? 'times'
    : sched.length === 1 && !sched[0].minuteMark
      ? 'interval'
      : sched.length
        ? 'marks'
        : 'asap';
  let when = cadenceWords({
    mode,
    times: sched.map((e) => e.time).filter(Boolean) as string[],
    intervalMinutes: sched[0]?.intervalMinutes || 60,
    marks: sched.map((e) => Number(e.minuteMark) || 0),
  });
  if (c.delayHours) {
    when = `${c.delayHours} ${c.delayHours === 1 ? 'hour' : 'hours'} after it’s published, then ${when}`;
  }
  const windows = c.windows || [];
  return {
    topics: `${topics}${but}${kinds}`,
    posts: `${POST_AS[c.postType || 'auto'] || c.postType}, for articles up to ${c.maxAgeHours ?? 24} hours old`,
    when: `${when.charAt(0).toUpperCase()}${when.slice(1)}`,
    onlyOn: windows.length
      ? onlyOnWords(
          windows.map((w) => w.dayOfWeek),
          windows.map((w) => [hm(w.startTime), hm(w.endTime)] as [string, string])
        )
      : null,
    legacy: c.legacyFilter || null,
  };
}

/**
 * mode: "campaigns" — top-level Campaigns, the dated ones (work with a start
 * and an end); "automatic" — Reach → Automatic, the always-on sharing rules,
 * which aren't really campaigns (backlog bf819be4, Prototype). Same screen,
 * one kind each; a link to the other kind (?campaign=, ?new=) goes there.
 */
export function ReachCampaigns({ mode = 'campaigns' }: { mode?: 'campaigns' | 'automatic' }) {
  const automatic = mode === 'automatic';
  const navigate = useNavigate();
  const { accountId, selectedAccount } = useAccount();
  // The Campaign role (2026-10-23): only the campaigns they're on, read
  // through campaignList (projects reaches the site's settings).
  const campaignRole = Boolean(selectedAccount?.campaignRole);
  const projectsQuery = useQuery<{ projects: Campaign[] }>(REACH_CAMPAIGNS, {
    variables: { accountId },
    skip: !accountId || campaignRole,
    fetchPolicy: 'cache-and-network',
  });
  const listQuery = useQuery<{ campaignList: Campaign[] }>(CAMPAIGN_LIST, {
    variables: { accountId },
    skip: !accountId || !campaignRole,
    fetchPolicy: 'cache-and-network',
  });
  const listData = listQuery.data;
  const data = useMemo(
    () =>
      campaignRole
        ? listData
          ? { projects: listData.campaignList }
          : undefined
        : projectsQuery.data,
    [campaignRole, listData, projectsQuery.data]
  );
  const { loading, error, refetch } = campaignRole ? listQuery : projectsQuery;
  // Nothing to show yet (the account is still resolving, or the first fetch is
  // out): bars, never "No campaigns yet" and its New campaign buttons.
  const pending = !data && !error && (loading || !accountId);
  const [approve] = useMutation<{
    approveCampaign: {
      approved: number;
      scheduled: number;
      undated: number;
      skippedChannels: string[];
    };
  }>(APPROVE_CAMPAIGN);
  const [updateProject] = useMutation(UPDATE_PROJECT);
  const [creating, setCreating] = useState(false);
  // A dated campaign's name, brief, end date and landing page, edited here.
  const [editingDated, setEditingDated] = useState<Campaign | null>(null);
  const [editing, setEditing] = useState<Campaign | null | 'new'>(null);
  // "Write a post" from a dated campaign: filed under it, on its channels.
  const [writingFor, setWritingFor] = useState<string | null>(null);
  // ?campaign=<id> (Today's "Review") opens that campaign.
  const [params, setParams] = useSearchParams();
  const [sel, setSel] = useState<string | null>(params.get('campaign'));
  // Reach header → "New campaign ▾" (Prototype v15) arrives as ?new=…
  const wanted = params.get('new');
  useEffect(() => {
    if (!wanted) return;
    // A new rule belongs to Automatic, a new campaign to Campaigns.
    if ((wanted === 'always') !== automatic) {
      navigate(`${automatic ? '/campaigns' : '/reach/automatic'}?new=${wanted}`, { replace: true });
      return;
    }
    if (wanted === 'dated') setCreating(true);
    else if (wanted === 'always') setEditing('new');
    else if (wanted === 'agent')
      openAskAgent({ action: 'campaign', onSelf: () => setCreating(true) });
    const next = new URLSearchParams(params);
    next.delete('new');
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted]);
  const [busy, setBusy] = useState(false);

  const all = useMemo(
    () =>
      [...(data?.projects || [])].sort(
        (a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0)
      ),
    [data]
  );
  // Dated campaigns by phase, empty groups left out: the soonest end first while
  // running or upcoming, the most recent end first once ended.
  const groups = useMemo(() => {
    const dated = all.filter((c) => c.kind !== 'always_on');
    // A draft: not ended, still planning, and nothing in it waiting on a yes.
    const groupOf = (c: Campaign): RailGroup => {
      const phase = phaseOf(c);
      return phase !== 'ended' &&
        c.status === 'planning' &&
        !c.posts.some((p) => p.status === 'in_review')
        ? 'draft'
        : phase;
    };
    return PHASES.map(([phase, label]) => ({
      phase,
      label,
      items: dated
        .filter((c) => groupOf(c) === phase)
        .sort((a, b) => (phase === 'ended' ? endTime(b) - endTime(a) : endTime(a) - endTime(b))),
    })).filter((g) => g.items.length > 0);
  }, [all]);
  const campaigns = groups.flatMap((g) => g.items);
  const alwaysOn = all.filter((c) => c.kind === 'always_on');

  // A campaign of the other kind (an old link, Today's "Review") opens where it lives.
  const picked = sel ? all.find((c) => c.id === sel) : null;
  const elsewhere = !!picked && (picked.kind === 'always_on') !== automatic;
  useEffect(() => {
    if (!elsewhere || !picked) return;
    navigate(
      `${automatic ? '/campaigns' : '/reach/automatic'}?campaign=${encodeURIComponent(picked.id)}`,
      {
        replace: true,
      }
    );
  }, [elsewhere, picked, automatic, navigate]);

  // Nothing picked: the first of this kind.
  const shown = automatic ? alwaysOn : campaigns;
  const current = (picked && !elsewhere ? picked.id : null) ?? shown[0]?.id ?? null;
  const campaign = all.find((c) => c.id === current) || null;

  // The open dated campaign's board: its containers, cards and team.
  const boardId = campaign && campaign.kind !== 'always_on' ? campaign.id : null;
  // The line under its goal: the brief and its posts against the voice.
  const { data: voiceData } = useQuery<{
    project: {
      briefCheck: VoiceCheck | null;
      posts: Array<{
        id: string;
        status: string;
        voiceCheck: VoiceCheck | null;
        targets: Array<{ platform: string }>;
      }>;
      recentPosts: Array<{ id: string; reachStatus: string; voiceCheck: VoiceCheck | null }>;
    } | null;
  }>(CAMPAIGN_VOICE, {
    variables: { id: boardId },
    skip: !boardId,
    fetchPolicy: 'cache-and-network',
  });
  const voiceChecked = [
    ...(voiceData?.project?.posts || []),
    ...(voiceData?.project?.recentPosts || []),
  ];
  const voiceLine = voiceData?.project
    ? campaignVoiceLine(
        voiceData.project.briefCheck,
        voiceChecked.map((p) => p.voiceCheck)
      )
    : null;
  // The first off-voice post's card on the board.
  const offFiled = voiceData?.project?.recentPosts.find((p) => p.voiceCheck?.offVoice);
  const offPlan = voiceData?.project?.posts.find((p) => p.voiceCheck?.offVoice);
  const firstOff = offFiled
    ? `post:${offFiled.id}`
    : offPlan
      ? `plan:${offPlan.id}:${offPlan.targets[0]?.platform || ''}`
      : null;
  const boardQuery = useQuery<{ campaignBoard: Board }>(CAMPAIGN_BOARD, {
    variables: { campaignId: boardId },
    skip: !boardId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const board =
    boardQuery.data?.campaignBoard && boardId ? (boardQuery.data.campaignBoard as Board) : null;
  const refreshBoard = async () => {
    await Promise.all([boardQuery.refetch().catch(() => null), refetch()]);
  };
  const approver = firstName(board?.approver?.name) || selectedAccount?.approverName || null;
  const canApprove = !campaignRole && (board ? board.viewer.canApprove : true);

  const inReview = (c: Campaign) => c.posts.filter((p) => p.status === 'in_review');
  // The open campaign, read the way the list reads it: an ended one can't be approved.
  const openEnded = !!campaign && campaign.kind !== 'always_on' && phaseOf(campaign) === 'ended';
  // Its channels whose connection expired, as Channels and the Calendar read
  // them; what's over needs no warning (backlog 33999dec).
  const { data: rowsData, refetch: refetchRows } = useQuery<{ socialProfiles: Channel[] }>(
    REACH_CHANNEL_ROWS,
    {
      variables: { accountId, since: todayWindow('week').from },
      skip: !accountId || !campaign,
      fetchPolicy: 'cache-and-network',
    }
  );
  const lostRows = useMemo(
    () =>
      openEnded
        ? []
        : (rowsData?.socialProfiles || []).filter((c) => c.isActive && connectionExpired(c)),
    [openEnded, rowsData]
  );
  const lostIds = useMemo(() => new Set(lostRows.map((c) => c.id)), [lostRows]);
  const { reconnect, dialogs: reconnectDialogs } = useChannelReconnect({
    accountId,
    onConnected: () => {
      void refetchRows();
      void refreshBoard();
    },
  });
  // A Campaign-role person sees that it expired; the channel isn't theirs to sign in.
  const reconnectById = campaignRole
    ? undefined
    : (id: string) => {
        const row = lostRows.find((c) => c.id === id);
        if (row) reconnect(row);
      };
  // Waiting on approval: the plan's posts, or posts filed under it (the board's).
  const boardWaiting =
    board?.containers.some((c) =>
      c.cards.some((cd) => (cd.type === 'post' || cd.type === 'plan') && cd.status === 'waiting')
    ) || false;
  const openWaiting = !!campaign && !openEnded && (inReview(campaign).length > 0 || boardWaiting);

  const approveCampaign = async (c: Campaign) => {
    setBusy(true);
    try {
      const { data: res } = await approve({ variables: { id: c.id } });
      await refetch();
      void boardQuery.refetch().catch(() => null);
      if (c.kind === 'always_on') {
        showInkToast(`${c.name} is on.`);
        return;
      }
      const r = res?.approveCampaign;
      const parts = [`Approved. ${r?.scheduled || 0} on the calendar.`];
      if (r?.undated)
        parts.push(`${r.undated} ${r.undated === 1 ? 'needs' : 'need'} a date before going out.`);
      if (r?.skippedChannels.length)
        parts.push(
          `${r.skippedChannels.map(channelName).join(', ')} posts need a photo or video, so those were left out.`
        );
      showInkToast(parts.join(' '));
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(false);
    }
  };

  /** A draft you set up yourself: Schedule campaign (or Start, once its start date passed). */
  const scheduleCampaign = async (c: Campaign, soon: boolean, startDay: string) => {
    setBusy(true);
    try {
      await approve({ variables: { id: c.id } });
      await refetch();
      void boardQuery.refetch().catch(() => null);
      showInkToast(
        soon ? `${c.name} is scheduled. It starts ${startDay}.` : `${c.name} has started.`
      );
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (c: Campaign, status: 'active' | 'paused' | 'completed') => {
    setBusy(true);
    try {
      await updateProject({ variables: { id: c.id, input: { status } } });
      await refetch();
      showInkToast(
        status === 'paused'
          ? `${c.name}: paused.`
          : status === 'active'
            ? `${c.name}: on again.`
            : `${c.name}: retired.`
      );
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(false);
    }
  };

  // Reach › Automatic › rule detail: take a migrated rule's older filter off.
  const removeOlderFilter = async (c: Campaign) => {
    if (
      !(await askInk({
        title: 'Remove the older filter?',
        body: 'The rule then posts every article its topics, posts and times allow.',
        note: 'It can’t be put back.',
        confirmLabel: 'Remove filter',
        danger: true,
      }))
    )
      return;
    setBusy(true);
    try {
      await updateProject({ variables: { id: c.id, input: { conditions: { filterSql: null } } } });
      await refetch();
      showInkToast(`${c.name}: older filter removed.`);
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(false);
    }
  };

  // On, but every channel it posts to was disconnected: it posts nothing.
  const alwaysOnTone = (c: Campaign) =>
    c.status === 'active' && disconnectedRuleChannels(c.channels).stalled
      ? { dot: 'var(--ink-amber)', color: 'var(--ink-amber)', label: 'Not posting' }
      : c.status === 'active'
        ? { dot: 'var(--ink-green)', color: 'var(--ink-green-ink)', label: 'Always on' }
        : c.status === 'planning'
          ? { dot: 'var(--ink-amber)', color: 'var(--ink-amber)', label: 'Not on yet' }
          : c.status === 'completed'
            ? { dot: 'var(--ink-line3)', color: 'var(--ink-ink3)', label: 'Retired' }
            : { dot: 'var(--ink-line3)', color: 'var(--ink-ink3)', label: 'Paused' };

  // Connected channels only: a disconnected one neither asks nor posts.

  // No campaigns at all (Prototype "Campaigns empty", feedback 10cb78ba): one
  // card with one way in, no list beside it; CampaignsSection hides the tabs
  // and the header's New campaign.
  const noCampaigns = !automatic && !pending && !(error && !data) && groups.length === 0;

  return (
    <div className={noCampaigns ? 'rcp rcp-zero' : 'rcp'}>
      {noCampaigns ? (
        <div className="rcp-zero-card">
          <div>
            <div className="rcp-zero-title">No campaigns yet</div>
            <p className="rcp-zero-copy">
              A campaign promotes one thing for a set time, like a price guide or a sale. It gets
              its own landing page, a sign-up form and posts planned around them. Nothing goes out
              until it’s scheduled.
            </p>
          </div>
          {campaignRole ? (
            <p className="rcp-zero-member">
              You’ll see campaigns here once someone adds you to one.
            </p>
          ) : (
            <div className="rcp-zero-actions">
              <AgentFirstButton
                action="campaign"
                label="New campaign"
                align="left"
                onSelf={() => setCreating(true)}
                footer={(close) => <CampaignMenuNote close={close} />}
              />
            </div>
          )}
        </div>
      ) : null}
      {noCampaigns ? null : (
        <>
          <aside className="rcp-rail">
            {automatic ? null : pending ? (
              <InkSkeleton rows={4} label="Loading campaigns" style={{ marginTop: 6 }} />
            ) : error && !data ? null : groups.length ? (
              groups.map((g, gi) => (
                <React.Fragment key={g.phase}>
                  <div className="pg-rail-head" style={gi ? { marginTop: 18 } : undefined}>
                    {g.label}
                  </div>
                  {g.items.map((c) => {
                    const ended = g.phase === 'ended';
                    const waiting = !ended && inReview(c).length > 0;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        className="pg-rail-item"
                        data-active={current === c.id ? 'true' : undefined}
                        onClick={() => setSel(c.id)}
                      >
                        <span
                          className="pg-dot"
                          style={{
                            background: waiting
                              ? 'var(--ink-amber)'
                              : g.phase === 'running'
                                ? 'var(--ink-green)'
                                : 'var(--ink-line3)',
                          }}
                        />
                        <span className="pg-rail-text">
                          <span className="pg-rail-title">{c.name}</span>
                          <span className="pg-rail-path">{campaignRange(c)}</span>
                        </span>
                        {waiting ? (
                          <span className="pg-rail-tag" style={{ color: 'var(--ink-amber)' }}>
                            Waiting
                          </span>
                        ) : g.phase === 'draft' ? (
                          <span className="pg-rail-tag" style={{ color: 'var(--ink-ink3)' }}>
                            Draft
                          </span>
                        ) : ended ? (
                          <span className="pg-rail-tag" style={{ color: 'var(--ink-ink3)' }}>
                            {endedAs(c).tag}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </React.Fragment>
              ))
            ) : (
              <>
                <div className="pg-rail-head">CAMPAIGNS</div>
                <p className="rcp-none">No campaigns yet.</p>
              </>
            )}
            {/* One way to start a rule: the section's "New rule" (Prototype). */}
            {automatic ? <div className="pg-rail-head">SHARING RULES</div> : null}
            {!automatic ? null : pending ? (
              <InkSkeleton rows={2} label="Loading sharing rules" />
            ) : error && !data ? null : alwaysOn.length ? (
              alwaysOn.map((c) => {
                const tone = alwaysOnTone(c);
                return (
                  <button
                    key={c.id}
                    type="button"
                    className="pg-rail-item"
                    data-active={current === c.id ? 'true' : undefined}
                    onClick={() => setSel(c.id)}
                  >
                    <span className="pg-dot" style={{ background: tone.dot }} />
                    <span className="pg-rail-text">
                      <span className="pg-rail-title">{c.name}</span>
                      <span className="pg-rail-path">{tone.label}</span>
                    </span>
                    {c.status === 'planning' ? (
                      <span className="pg-rail-tag" style={{ color: 'var(--ink-amber)' }}>
                        Turn on
                      </span>
                    ) : c.status !== 'completed' &&
                      disconnectedRuleChannels(c.channels).off.length ? (
                      <span className="pg-rail-tag" style={{ color: 'var(--ink-amber)' }}>
                        Reconnect
                      </span>
                    ) : null}
                  </button>
                );
              })
            ) : // Empty: the main card says so, once (backlog fe4cb61c); the note stays.
            null}
            {automatic ? (
              <p className="rcp-rail-note">
                Rules run on their own and never end. Work with a start and an end date lives in
                Campaigns.
              </p>
            ) : null}
          </aside>

          <section className="rcp-main">
            {pending ? (
              <InkSkeleton rows={5} label="Loading campaign" />
            ) : error && !data ? (
              <div className="ink-muted" role="alert" style={{ color: 'var(--ink-red)' }}>
                Campaigns couldn’t load just now.{' '}
                <button type="button" className="ink-quiet-link" onClick={() => void refetch()}>
                  Try again
                </button>
              </div>
            ) : automatic && editing ? (
              <AlwaysOnEditor
                campaign={editing === 'new' ? null : editing}
                onClose={() => setEditing(null)}
                onSaved={(id) => {
                  setEditing(null);
                  if (id) setSel(id);
                  void refetch();
                }}
              />
            ) : campaign && campaign.kind === 'always_on' ? (
              <AlwaysOnDetail
                campaign={campaign}
                tone={alwaysOnTone(campaign)}
                busy={busy}
                onTurnOn={() => void approveCampaign(campaign)}
                onPause={() => void setStatus(campaign, 'paused')}
                onResume={() => void setStatus(campaign, 'active')}
                onEdit={() => setEditing(campaign)}
                onRemoveOlderFilter={() => void removeOlderFilter(campaign)}
                lostChannels={lostRows}
                onReconnect={reconnectById}
              />
            ) : campaign ? (
              (() => {
                // What approving puts on the calendar: the board's waiting posts
                // (the plan's and any filed under it), or the plan's alone.
                const waitingCount =
                  board?.containers
                    .flatMap((c) => c.cards)
                    .filter(
                      (c) => (c.type === 'post' || c.type === 'plan') && c.status === 'waiting'
                    ).length || inReview(campaign).length;
                const tone = openEnded
                  ? {
                      dot: 'var(--ink-line3)',
                      color: 'var(--ink-ink3)',
                      label: endedAs(campaign).status,
                    }
                  : openWaiting
                    ? {
                        dot: 'var(--ink-amber)',
                        color: 'var(--ink-amber)',
                        label: canApprove ? TO_APPROVE : `Waiting on ${approver || 'the owner'}`,
                      }
                    : campaign.status === 'active'
                      ? phaseOf(campaign) === 'running'
                        ? {
                            dot: 'var(--ink-green)',
                            color: 'var(--ink-green-ink)',
                            label: 'Running',
                          }
                        : {
                            dot: 'var(--ink-accent)',
                            color: 'var(--ink-accent)',
                            label: 'Scheduled',
                          }
                      : {
                          dot: 'var(--ink-line3)',
                          color: 'var(--ink-ink3)',
                          label: CAMPAIGN_STATUS[campaign.status] || campaign.status,
                        };
                const cards = board?.containers.flatMap((c) => c.cards) || [];
                const landing = cards.find((c) => c.type === 'landing');
                const form = cards.find((c) => c.type === 'form');
                // The channels it posts on, as the board shows them (UX review
                // round 20: "None yet" beside a board with a Facebook column):
                // the ones picked, plus any with posts; with neither, the
                // board's own channel columns.
                const columns = (board?.containers || []).filter((c) => c.type === 'channel');
                // Its channels, and any a post of it uses: the board's columns (Round 44 §4).
                const channelNames = [
                  ...new Set(
                    [
                      ...campaign.channels,
                      ...columns.map((c) => ({
                        platform: c.platform || '',
                        profileName: c.name,
                        profileUsername: c.username,
                      })),
                    ].map(channelAccount)
                  ),
                ].join(', ');
                const created = toDate(campaign.createdAt);
                return (
                  <>
                    <div className="cb-title-row">
                      <div style={{ minWidth: 0 }}>
                        <div className="ccp-title">
                          <CampaignColorPicker
                            campaign={campaign}
                            canChange={!campaignRole}
                            onChanged={() => void refetch()}
                          />
                          <h2 className="rcp-title">{campaign.name}</h2>
                        </div>
                        <div className="cb-meta">
                          <span className="cb-meta-status" style={{ color: tone.color }}>
                            <span className="cb-dot" style={{ background: tone.dot }} />
                            {tone.label}
                          </span>
                          <span>·</span>
                          <span>{campaignRange(campaign)}</span>
                          <span>·</span>
                          <span>
                            {campaign.createdBy ? 'Set up' : 'Drafted by your agent'}
                            {created ? ` · ${dayTime(created)}` : ''}
                          </span>
                        </div>
                      </div>
                      {canApprove ? (
                        <AgentFirstButton
                          action="campaignEdit"
                          ctx={{ title: campaign.name }}
                          label="Edit campaign"
                          className="ink-btn"
                          wrapClassName="rcp-edit-wrap"
                          onSelf={() => setEditingDated(campaign)}
                        />
                      ) : null}
                    </div>
                    <CampaignTeamRow
                      campaign={campaign}
                      board={board}
                      onChanged={() => void boardQuery.refetch()}
                    />
                    <CampaignLinksRow
                      campaign={campaign}
                      board={board}
                      onChanged={() => void boardQuery.refetch()}
                    />
                    {openEnded && neverApproved(campaign) ? (
                      <p className="rcp-story">
                        {campaign.deadline
                          ? `Ended ${day(campaign.deadline)} without approval, so nothing went out.`
                          : 'Ended without approval, so nothing went out.'}
                      </p>
                    ) : null}
                    {openWaiting ? (
                      <div className="cb-approve">
                        <span
                          className="cb-dot"
                          style={{ background: 'var(--ink-amber)', width: 8, height: 8 }}
                        />
                        <div style={{ flex: 1, minWidth: 220 }}>
                          <div className="cb-approve-title">
                            {campaign.createdBy
                              ? `${waitingCount} ${waitingCount === 1 ? 'post is' : 'posts are'} waiting for approval`
                              : 'Your agent drafted this campaign'}
                          </div>
                          <div className="cb-approve-note">
                            Approving schedules{' '}
                            {waitingCount === 1 ? 'its post' : `all ${waitingCount} posts`}. You can
                            still skip any post on the calendar.
                          </div>
                        </div>
                        {canApprove ? (
                          <button
                            type="button"
                            className="ink-btn ink-btn-primary"
                            disabled={busy}
                            onClick={() => void approveCampaign(campaign)}
                          >
                            Approve campaign
                          </button>
                        ) : (
                          <span className="cb-approve-who">
                            {board?.approver?.name || approver || 'The owner'} approves it
                          </span>
                        )}
                      </div>
                    ) : !openEnded && campaign.status === 'planning' ? (
                      // Set up but not scheduled (Prototype; feedback 71704394).
                      (() => {
                        const start = campaignStart(campaign);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const soon = Boolean(start && start.getTime() > today.getTime());
                        const startDay = shortDate(start);
                        // What scheduling really does (backlog 33236a52): a landing
                        // page already live (the home page, an address elsewhere)
                        // doesn't "go live", and with no posts nothing goes out.
                        const landingLive = Boolean(
                          landing &&
                            (landing.status === 'approved' || landing.status === 'elsewhere')
                        );
                        const landingName =
                          landing?.landing?.title ||
                          landing?.landing?.path ||
                          (landing?.landing?.url || '').replace(/^https?:\/\//, '');
                        // The posts still to go out, by channel (feedback 3cc16e85).
                        const gone = new Set(['skipped', 'posted', 'publishing', 'missed']);
                        const postsOn = (lost: boolean) =>
                          (board?.containers || []).flatMap((col) =>
                            col.type === 'channel' &&
                            lostIds.has(col.socialProfileId || '') === lost
                              ? col.cards
                                  .filter(
                                    (c) =>
                                      (c.type === 'post' || c.type === 'plan') &&
                                      !gone.has(c.status)
                                  )
                                  .map(() => channelName(col.platform || ''))
                              : []
                          );
                        const lostPosts = postsOn(true);
                        const draft = draftCampaignWords({
                          byAgent: !campaign.createdBy,
                          startDay,
                          landing: landing ? { live: landingLive, name: landingName } : null,
                          posts: postsOn(false),
                          lostPosts,
                        });
                        return (
                          <div className="cb-approve">
                            <span
                              className="cb-dot"
                              style={{ background: 'var(--ink-ink3)', width: 8, height: 8 }}
                            />
                            <div style={{ flex: 1, minWidth: 220 }}>
                              <div className="cb-approve-title">{draft.title}</div>
                              <div className="cb-approve-note">
                                {soon
                                  ? draft.note
                                  : [
                                      `It was set to start ${startDay}. Start it now, or change the dates first.`,
                                      lostChannelWords(lostPosts),
                                    ]
                                      .filter(Boolean)
                                      .join(' ')}
                              </div>
                            </div>
                            {canApprove ? (
                              <button
                                type="button"
                                className="ink-btn ink-btn-primary"
                                disabled={busy}
                                onClick={() => void scheduleCampaign(campaign, soon, startDay)}
                              >
                                {soon ? 'Schedule campaign' : 'Start campaign'}
                              </button>
                            ) : (
                              <span className="cb-approve-who">
                                {board?.approver?.name || approver || 'The owner'} schedules it
                              </span>
                            )}
                          </div>
                        );
                      })()
                    ) : null}
                    {campaign.campaignStory ? (
                      <p className="rcp-story cb-goal">{campaign.campaignStory}</p>
                    ) : null}
                    {voiceLine ? (
                      <div className="rcp-voice-line">
                        <span
                          style={{
                            color: voiceLine.warn ? 'var(--ink-amber)' : 'var(--ink-green-ink)',
                          }}
                        >
                          {voiceLine.text}
                        </span>
                        {firstOff ? (
                          <button
                            type="button"
                            className="ink-quiet-link"
                            onClick={() => {
                              const next = new URLSearchParams(params);
                              next.set('card', firstOff);
                              setParams(next, { replace: true });
                            }}
                          >
                            Review the post →
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                    {board && accountId ? (
                      <CampaignBoardView
                        accountId={accountId}
                        campaign={campaign}
                        board={board}
                        ended={openEnded}
                        onChanged={refreshBoard}
                        lostChannels={lostIds}
                        onReconnect={reconnectById}
                        openCardKey={params.get('card')}
                        onCardOpened={() => {
                          const next = new URLSearchParams(params);
                          next.delete('card');
                          setParams(next, { replace: true });
                        }}
                      />
                    ) : boardQuery.error && !board ? (
                      <div className="ink-muted" role="alert" style={{ marginTop: 24 }}>
                        The campaign’s work couldn’t load just now.{' '}
                        <button
                          type="button"
                          className="ink-quiet-link"
                          onClick={() => void boardQuery.refetch()}
                        >
                          Try again
                        </button>
                      </div>
                    ) : (
                      <InkSkeleton
                        rows={4}
                        label="Loading the campaign’s work"
                        style={{ marginTop: 28 }}
                      />
                    )}
                    <div className="rcp-facts cb-facts">
                      {landing ? (
                        <div>
                          <span>Landing page</span>
                          <span>
                            {landing.landing?.kind === 'page'
                              ? `${landing.landing.headline || landing.landing.title || 'Untitled'} · ${landing.landing.path}`
                              : landing.landing?.kind === 'agent'
                                ? 'Your agent drafts it from the brief'
                                : (landing.landing?.url || '').replace(/^https?:\/\//, '')}
                          </span>
                          {canApprove && landing.pageId ? (
                            <Link
                              to={`/content?view=pages&page=${landing.pageId}`}
                              className="cb-facts-link"
                            >
                              Open in Pages →
                            </Link>
                          ) : landing.landing?.kind === 'url' && landing.landing.url ? (
                            <a
                              href={landing.landing.url}
                              target="_blank"
                              rel="noreferrer"
                              className="cb-facts-link"
                            >
                              Open ↗
                            </a>
                          ) : (
                            <span />
                          )}
                        </div>
                      ) : campaign.landingPageUrl ? (
                        <div>
                          <span>Landing page</span>
                          <a href={campaign.landingPageUrl} target="_blank" rel="noreferrer">
                            {campaign.landingPageUrl.replace(/^https?:\/\//, '')} ↗
                          </a>
                          <span />
                        </div>
                      ) : null}
                      {form?.form ? (
                        <div>
                          <span>Form</span>
                          <span>{form.text}</span>
                          {canApprove && form.formId ? (
                            <Link
                              to={`/content?view=forms&form=${form.formId}`}
                              className="cb-facts-link"
                            >
                              Open in Forms →
                            </Link>
                          ) : (
                            <span />
                          )}
                        </div>
                      ) : null}
                      <div>
                        <span>Channels</span>
                        <span>
                          {channelNames || 'None yet'}
                          {campaign.status === 'active' ? (
                            <LostChannelsNote
                              channels={columns
                                .filter((c) => lostIds.has(c.socialProfileId || ''))
                                .map((c) => ({
                                  id: c.socialProfileId || c.key,
                                  platform: c.platform || '',
                                }))}
                              onReconnect={reconnectById}
                            />
                          ) : null}
                        </span>
                        <span />
                      </div>
                      {campaign.deadline ? (
                        <div>
                          <span>Ends</span>
                          <span>{day(campaign.deadline)}</span>
                          <span />
                        </div>
                      ) : null}
                    </div>
                    <CampaignResults campaignId={campaign.id} />
                  </>
                );
              })()
            ) : automatic ? (
              <div className="rc-empty">
                <div className="rc-empty-title">No sharing rules yet</div>
                <p>
                  A sharing rule posts your new articles to your channels as they’re published, on
                  the days and at the times you choose. Each post asks you first until you change
                  that in Approvals. Start one with New rule, at the top.
                </p>
              </div>
            ) : (
              <div className="rc-empty">
                <div className="rc-empty-title">No campaigns yet</div>
                <p>
                  A campaign groups the posts for one push, like a launch or a sale, with its own
                  dates, and can have its own landing page and form. Ask your agent to plan one, or
                  set it up yourself.
                </p>
                <div className="rcp-actions">
                  <AgentFirstButton
                    action="campaign"
                    label="New campaign"
                    align="left"
                    onSelf={() => setCreating(true)}
                    footer={(close) => <CampaignMenuNote close={close} />}
                  />
                </div>
              </div>
            )}
          </section>
        </>
      )}

      {creating || editingDated ? (
        <CampaignEditor
          campaign={editingDated}
          onClose={() => {
            setCreating(false);
            setEditingDated(null);
          }}
          onSaved={(id) => {
            setCreating(false);
            setEditingDated(null);
            if (id) setSel(id);
            void refetch();
          }}
        />
      ) : null}
      {writingFor ? (
        <WritePost
          campaignId={writingFor}
          onClose={() => setWritingFor(null)}
          onPosted={() => {
            setWritingFor(null);
            void refetch();
          }}
        />
      ) : null}
      {reconnectDialogs}
    </div>
  );
}

/**
 * " · Facebook connection expired · Reconnect" on a campaign's Channels row
 * (backlog 33999dec). One channel signs in here; several go to Channels.
 */
function LostChannelsNote({
  channels,
  onReconnect,
}: {
  channels: Array<{ id: string; platform: string }>;
  onReconnect?: (id: string) => void;
}) {
  const navigate = useNavigate();
  if (!channels.length) return null;
  const many = channels.length > 1;
  return (
    <>
      <span style={{ color: 'var(--ink-amber)' }}>
        {' · '}
        {andList([...new Set(channels.map((c) => channelName(c.platform)))])} connection
        {many ? 's' : ''} expired
      </span>
      {onReconnect ? (
        <>
          {' · '}
          <button
            type="button"
            className="ink-quiet-link"
            onClick={() => (many ? navigate('/reach/channels') : onReconnect(channels[0].id))}
          >
            Reconnect
          </button>
        </>
      ) : null}
    </>
  );
}

function AlwaysOnDetail({
  campaign,
  tone,
  busy,
  onTurnOn,
  onPause,
  onResume,
  onEdit,
  onRemoveOlderFilter,
  lostChannels,
  onReconnect,
}: {
  campaign: Campaign;
  tone: { dot: string; color: string; label: string };
  busy: boolean;
  onTurnOn: () => void;
  onPause: () => void;
  onResume: () => void;
  onEdit: () => void;
  onRemoveOlderFilter: () => void;
  lostChannels: Channel[];
  onReconnect?: (id: string) => void;
}) {
  const facts = describeConditions(campaign.conditions);
  const { off, stalled } = disconnectedRuleChannels(campaign.channels);
  const offList = [...new Set(off.map((c) => channelName(c.platform)))];
  const offNames = andList(offList);
  const onNames = andList([
    ...new Set(
      campaign.channels.filter((c) => c.isActive !== false).map((c) => channelName(c.platform))
    ),
  ]);
  const posts = campaign.recentPosts || [];
  const posted = posts.filter((p) => p.reachStatus === 'posted').length;
  // The rule in one sentence, as its form's IN SHORT box says it (Prototype).
  const sentence = sentenceFromConditions(
    campaign.conditions,
    campaign.channels.map((c) => channelName(c.platform))
  );
  return (
    <>
      <div className="cb-title-row">
        <div style={{ minWidth: 0 }}>
          <h2 className="rcp-title">{campaign.name}</h2>
          {/* "● On · Always on · Set up by you · Aug 30" (Prototype). */}
          <div className="cb-meta">
            <span className="cb-meta-status" style={{ color: tone.color }}>
              <span className="cb-dot" style={{ background: tone.dot }} />
              {campaign.status === 'active' && !stalled ? 'On' : tone.label}
            </span>
            <span>·</span>
            <span>Always on</span>
            <span>·</span>
            <span>
              {campaign.createdBy ? 'Set up by you' : 'Proposed by your agent'} ·{' '}
              {day(campaign.createdAt)}
            </span>
          </div>
        </div>
        <span className="rcp-head-actions">
          {campaign.status === 'active' ? (
            <button type="button" className="ink-btn" disabled={busy} onClick={onPause}>
              Pause
            </button>
          ) : campaign.status === 'paused' ? (
            <button type="button" className="ink-btn" disabled={busy} onClick={onResume}>
              Resume
            </button>
          ) : null}
          <button type="button" className="ink-btn" disabled={busy} onClick={onEdit}>
            Edit
          </button>
        </span>
      </div>
      {campaign.status === 'planning' ? (
        <div className="rcp-banner">
          <span className="ink-dot" style={{ background: 'var(--ink-amber)' }} />
          <div style={{ flex: 1 }}>
            <strong>Not on yet</strong>
            <div className="ink-muted">
              Nothing posts until you turn it on. Check the channels and conditions first.
            </div>
          </div>
          <button
            type="button"
            className="ink-btn ink-btn-primary"
            disabled={busy}
            onClick={onTurnOn}
          >
            Turn it on
          </button>
        </div>
      ) : null}
      {off.length > 0 && campaign.status !== 'planning' && campaign.status !== 'completed' ? (
        // A channel it posts to was disconnected (QA round 23): say so where
        // the rule reads "On", with the way back as the main action.
        <div className="rcp-banner" role="status">
          <span className="ink-dot" style={{ background: 'var(--ink-amber)' }} />
          <div style={{ flex: 1 }}>
            <strong>
              {offNames} {offList.length === 1 ? 'isn’t' : 'aren’t'} connected
            </strong>
            <div className="ink-muted">
              {stalled
                ? 'Nothing posts from this rule until you reconnect.'
                : `This rule posts only to ${onNames} until you reconnect.`}
            </div>
          </div>
          <Link to="/reach/channels" className="ink-btn ink-btn-primary">
            Reconnect
          </Link>
        </div>
      ) : null}
      <p className="rcp-story cb-goal">{sentence}</p>
      {campaign.campaignStory ? <p className="ink-muted">{campaign.campaignStory}</p> : null}
      <div className="rcp-facts" style={{ marginTop: 20 }}>
        <div>
          <span>Articles</span>
          <span>{facts.topics}</span>
        </div>
        <div>
          <span>Posts</span>
          <span>{facts.posts}</span>
        </div>
        <div>
          <span>When</span>
          <span>{facts.when}</span>
        </div>
        {facts.onlyOn ? (
          <div>
            <span>Only on</span>
            <span>{facts.onlyOn}</span>
          </div>
        ) : null}
        <div>
          <span>Channels</span>
          <span>
            {campaign.channels.length
              ? campaign.channels
                  .map(
                    (c) =>
                      channelLabel(c, campaign.channels) +
                      (c.isActive === false
                        ? ' (not connected)'
                        : c.isPublishing === false
                          ? ' (publishing off)'
                          : '')
                  )
                  .join(', ')
              : 'None — pick one under Change'}
            {campaign.status === 'active' ? (
              <LostChannelsNote
                channels={lostChannels.filter((c) => campaign.channels.some((x) => x.id === c.id))}
                onReconnect={onReconnect}
              />
            ) : null}
          </span>
        </div>
        {facts.legacy ? (
          <div>
            <span>Older filter</span>
            <span>
              {legacyFilterWords(facts.legacy, campaign.conditions?.include || [])}{' '}
              <button
                type="button"
                className="ink-quiet-link"
                disabled={busy}
                onClick={onRemoveOlderFilter}
              >
                Remove
              </button>
            </span>
          </div>
        ) : null}
        <div>
          <span>Approval</span>
          <span>
            <ApprovalChip
              mode={campaign.approvalMode === 'out' ? 'out' : 'ask'}
              to={approvalsAt(ruleAnchor(campaign.id))}
              suffix=" · Change in Approvals"
            />
          </span>
        </div>
      </div>
      <CampaignResults campaignId={campaign.id} />
      <div className="rcp-h3-row">
        <h3 className="rcp-h3">
          {posts.length ? `Last ${posts.length} ${posts.length === 1 ? 'post' : 'posts'}` : 'Posts'}
          {posted ? <span className="ink-muted"> · {posted} posted</span> : null}
        </h3>
        <Link to="/reach/calendar" className="ink-quiet-link">
          Open the calendar
        </Link>
      </div>
      {posts.length ? (
        <div className="rcp-posts">
          {posts.map((p) => {
            const st = REACH_STATUS[p.reachStatus] || REACH_STATUS.draft;
            const t = postTime(p);
            return (
              <div className="rcp-post" key={p.id}>
                <span className="rcp-post-when">{t ? dayTime(t) : 'No date'}</span>
                <span className="rcp-post-marks">
                  <span className="rc-mark rc-mark-sm" title={channelName(p.platform)}>
                    {channelMark(p.platform)}
                  </span>
                </span>
                <span className="rcp-post-text">
                  <span className="rcp-post-idea">{postText(p) || 'Untitled post'}</span>
                </span>
                <span className="rcp-post-status" style={{ color: st.color }}>
                  {st.label}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="ink-muted">
          {campaign.status === 'active'
            ? 'No posts yet. The next matching article gets one.'
            : 'No posts yet.'}
        </p>
      )}
    </>
  );
}
